import { Button } from "components/buttons/Button";
import { Link } from "react-router-dom";
import { INiche } from "@/common/services/types";
import { Image } from "components";
import { routes } from "@/common/utils/routes";
import { nFormatter } from "@/common/utils/parseNumber";
import { RefObject, memo } from "react";
import { useMediaQuery } from "usehooks-ts";
import { TABLET_WIDTH } from "@/common/utils/device";
import DEFAULT_USERPIC from "@/common/images/icons/default_userpic.svg";
import BROKEN_IMAGE from "@/common/images/icons/image_icon.svg";
import UsersIcon from "@/common/images/icons/users.svg?react";
import RightArrowIcon from "@/common/images/icons/right_arrow.svg?react";
import "./NichePreview.scss";

interface INichePreviewProps {
  niche: INiche;
  following: boolean;
  isLink?: boolean;
  withPreview?: boolean;
  previewNicheRef?: RefObject<HTMLDivElement>;
  isVisible: boolean;
  onFollow: () => void;
  onError: (gif: string) => void;
  onPreviewClick?: () => void;
}

export const NichePreview: React.FC<INichePreviewProps> = memo(({
  niche,
  following,
  withPreview,
  isLink,
  previewNicheRef,
  isVisible,
  onFollow,
  onError,
  onPreviewClick
}) => {
  const isDesktop = useMediaQuery(`(min-width: ${TABLET_WIDTH}px)`);

  const renderFollowButton = () => (
    <Button variant={following ? "secondary" : "primary"} size={isDesktop ? "s" : "xs"} onClick={onFollow}>
      {following ? "Leave" : "Join"}
    </Button>
  );

  const renderLinkIcon = () => (
    <Link
      className="Button Button_secondary Button_isRound Button_xs"
      to={routes.niche(niche.id)}
      onClick={onPreviewClick}
      aria-label={`Link to ${routes.niche(niche.id)}`}
    >
      <RightArrowIcon />
    </Link>
  );

  const renderNicheThumbnail = () => (
    <Link className="NicheAvatar" to={routes.niche(niche.id)} onClick={onPreviewClick} aria-label={`Link to ${niche.name} niche`}>
      <Image src={niche.thumbnail ?? DEFAULT_USERPIC} alt={`Avatar for ${niche.name} niche`} />
    </Link>
  );

  const renderNichePreviews = () => {
    if (!withPreview || !niche.previews) {
      return null;
    }

    return (
      niche.previews.slice(0, 3).map((item, index) => (
        <Link key={index} to={routes.niche(niche.id)} onClick={onPreviewClick} aria-label={`Link to ${niche.id}`}>
          <Image src={item.thumbnail ?? BROKEN_IMAGE} alt={`Preview for ${niche.id}`} onFailure={() => onError(item.id)} />
        </Link>
      ))
    );
  };

  return (
    <div className="NichePreview" ref={previewNicheRef}>
      {isVisible ? (
        <>
          <div className="NichePreview-Header">
            {renderNicheThumbnail()}
            <div className="NicheInfo">
              <Link to={routes.niche(niche.id)} onClick={onPreviewClick}>{niche.name}</Link>
              <div className="NicheSubscribes">
                <UsersIcon />
                <span>{nFormatter(niche.subscribers)}</span>
              </div>
            </div>
            <div className="NicheButton">
              {!isLink ? renderFollowButton() : renderLinkIcon()}
            </div>
          </div>
          <div className="NichePreview-Footer">
            {renderNichePreviews()}
          </div>
        </>
      ) : null}
    </div>
  );
});
